import { SidebarList } from "@/lib/Components/SideBarList/SideBarList";
import { CarIcon } from "lucide-react";
import {
  BuildingOffice2Icon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";
import { formatDate } from "@/lib/Formatters/formatDate";
import { formatDateTimeSinceNow } from "@/lib/Formatters/formatDateTimeSinceNow";
import { useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { relocationsRoute } from "@/app/Relocations/Routes/relocationRoute";
import { useInfiniteGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  RelocationListItem,
  relocationListQuery,
} from "@/app/Relocations/GraphQL/relocationListQuery";
import { getRelocationListQueryVariables } from "@/app/Relocations/Utils/getRelocationListQueryVariables";
import { Badge } from "@/components/catalyst/badge";
import { relocationStatusColorMap } from "@/app/Relocations/Utils/relocationStatusColorMap";
import { useTranslations } from "use-intl";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { Highlighter } from "@/lib/Components/Highlighter";
import { SupplierIcon } from "@/app/Suppliers/Components/SupplierIcon";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { RelocationStatus } from "@/gql/graphql";

export function RelocationListScreen() {
  const searchParams = useSearch({ from: relocationsRoute.id });
  const [search, setSearch] = useState(searchParams.relocationSearch ?? "");
  const { supplierId } = useGlobalSupplier();
  const queryVariables = getRelocationListQueryVariables(
    {
      ...searchParams,
      relocationSearch: search,
    },
    supplierId,
  );
  const { data, fetchNextPage, isFetchingNextPage, isFetching } =
    useInfiniteGqlQuery(relocationListQuery, queryVariables, {
      getNextPageParam: (prevPage) => {
        return prevPage.relocations.paginatorInfo.hasMorePages
          ? {
              page: prevPage.relocations.paginatorInfo.currentPage + 1,
            }
          : undefined;
      },
      initialPageParam: {
        page: 1,
      },
    });

  const items = data?.pages.flatMap((p) => p.relocations.data);
  const totalCount = data?.pages.at(0)?.relocations.paginatorInfo.total ?? 0;

  return (
    <SidebarList
      createRoute={"/$tenant/$orgId/supplier/{-$supplierId}/relocations/create"}
      listRoute={"/$tenant/$orgId/supplier/{-$supplierId}/relocations"}
      search={search}
      items={items}
      itemNode={(item) => <RelocationItem model={item} search={search} />}
      isFetchingNextPage={isFetchingNextPage}
      isFetching={isFetching}
      onSearchChange={(newStr) => {
        setSearch(newStr);
      }}
      onClearFilters={() => {
        setSearch("");
      }}
      totalCount={totalCount}
      linkProps={(item) => ({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/relocations/$relocationId",
        params: (prev) => ({
          ...prev,
          relocationId: item.id,
        }),
      })}
      fetchNextPage={fetchNextPage}
    />
  );
}

function RelocationItem({
  model,
  search,
}: {
  model: RelocationListItem;
  search: string;
}) {
  const t = useTranslations("relocation");
  const { hasSupplier } = useGlobalSupplier();

  return (
    <div className="flex justify-between space-x-2">
      <div className="grow space-y-1">
        <div className="truncate text-xs font-semibold">
          <p>
            <Highlighter searchString={search}>
              {model.departureOffice.name}
            </Highlighter>
          </p>
          <p className="flex items-center gap-1">
            <Highlighter searchString={search}>
              {model.deliveryOffice.name}
            </Highlighter>
            {model.linkedRelocationCount > 0 ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge className="bg-blue-50 text-xs text-blue-700">
                    +{model.linkedRelocationCount}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="space-y-0.5">
                    {model.group.relocations
                      .filter(
                        (r) =>
                          r.id !== model.id &&
                          r.status !== RelocationStatus.Archived,
                      )
                      .map((r) => (
                        <p key={r.id}>
                          <span className="text-gray-400">or</span>{" "}
                          {r.deliveryOffice.name}
                        </p>
                      ))}
                  </div>
                </TooltipContent>
              </Tooltip>
            ) : null}
          </p>
        </div>

        <p className="flex items-center truncate text-xs text-gray-500">
          <CalendarDaysIcon className="mr-2 inline h-4 w-4 shrink-0" />
          {`${formatDate(model.available_from_date)} - ${formatDate(
            model.available_to_date,
          )}`}
        </p>

        {!hasSupplier ? (
          <p className="flex items-center truncate text-xs text-gray-500">
            <SupplierIcon
              model={model.supplier}
              className="mr-2 size-4 shrink-0 rounded-sm"
              fallback={
                <BuildingOffice2Icon className="mr-2 inline h-4 w-4 shrink-0" />
              }
            />
            <Highlighter searchString={search}>
              {model.supplier.name}
            </Highlighter>
          </p>
        ) : null}

        <p className="flex items-center truncate text-xs text-gray-500">
          <CarIcon className="mr-2 inline h-4 w-4 shrink-0" />
          <Highlighter searchString={search}>{model.vehicle.name}</Highlighter>
        </p>

        <p>
          <time
            dateTime={model.created_at}
            className="text-xs italic text-gray-500"
          >
            {formatDateTimeSinceNow(model.created_at)}
          </time>
        </p>
      </div>

      <div className="flex min-w-0 shrink-0 flex-col items-end gap-2 self-start">
        <Badge
          color={relocationStatusColorMap[model.status]}
          className="capitalize"
        >
          {t(`status.${model.status}`)}
        </Badge>
        {model.ogImage ? (
          <div className="size-16 overflow-hidden rounded-md">
            <img
              src={model.ogImage.url}
              alt={`${model.departureOffice.name} to ${model.deliveryOffice.name}`}
              className="h-full w-full object-cover"
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
